export const APP_NAME = 'CRM';

export const API_PREFIX = '/api';

export const BUSINESS_TYPES = ['BEAUTY_SALON', 'BARBERSHOP', 'DENTAL_CLINIC'] as const;

export type BusinessTypeValue = (typeof BUSINESS_TYPES)[number];

export const BUSINESS_TYPE_LABELS: Record<BusinessTypeValue, string> = {
  BEAUTY_SALON: 'Salao de beleza',
  BARBERSHOP: 'Barbearia',
  DENTAL_CLINIC: 'Clinica odontologica',
};

export const UNIT_TYPES = ['HEAD_OFFICE', 'BRANCH'] as const;

export type UnitTypeValue = (typeof UNIT_TYPES)[number];

export const UNIT_TYPE_LABELS: Record<UnitTypeValue, string> = {
  HEAD_OFFICE: 'Matriz',
  BRANCH: 'Filial',
};

export const APPOINTMENT_STATUSES = [
  'SCHEDULED',
  'CONFIRMED',
  'IN_PROGRESS',
  'COMPLETED',
  'CANCELLED',
  'NO_SHOW',
] as const;

export type AppointmentStatusValue = (typeof APPOINTMENT_STATUSES)[number];

export const APPOINTMENT_STATUS_LABELS: Record<AppointmentStatusValue, string> = {
  SCHEDULED: 'Agendado',
  CONFIRMED: 'Confirmado',
  IN_PROGRESS: 'Em andamento',
  COMPLETED: 'Concluido',
  CANCELLED: 'Cancelado',
  NO_SHOW: 'Desistencia',
};

/** Status que ainda ocupam a agenda (contam para conflito de horario). */
export const ACTIVE_APPOINTMENT_STATUSES = [
  'SCHEDULED',
  'CONFIRMED',
  'IN_PROGRESS',
] as const satisfies readonly AppointmentStatusValue[];

// ---------------------------------------------------------------------------
// Financeiro
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Recebiveis
// ---------------------------------------------------------------------------

export const PAYMENT_METHODS = ['CASH', 'PIX', 'DEBIT', 'CREDIT'] as const;

export type PaymentMethodValue = (typeof PAYMENT_METHODS)[number];

export const PAYMENT_METHOD_LABELS: Record<PaymentMethodValue, string> = {
  CASH: 'Dinheiro',
  PIX: 'Pix',
  DEBIT: 'Debito',
  CREDIT: 'Credito',
};

/** Dinheiro nao passa por maquininha - nem taxa, nem prazo, nem parcela. */
export const CARD_PAYMENT_METHODS = ['PIX', 'DEBIT', 'CREDIT'] as const;

export const RECEIVABLE_STATUSES = ['PENDING', 'RECEIVED', 'CANCELLED'] as const;

export type ReceivableStatusValue = (typeof RECEIVABLE_STATUSES)[number];

export const RECEIVABLE_STATUS_LABELS: Record<ReceivableStatusValue, string> = {
  PENDING: 'A receber',
  RECEIVED: 'Recebido',
  CANCELLED: 'Cancelado',
};

/** Ate quantas vezes o salao costuma parcelar. */
export const MAX_INSTALLMENTS = 12;

/**
 * As adquirentes que o sistema ja conhece. E catalogo do produto, nao dado do
 * tenant: o salao so marca quais usa, e uma nova entra aqui sem mexer em
 * nenhuma empresa.
 */
export const DEFAULT_ACQUIRERS: readonly { name: string; slug: string }[] = [
  { name: 'Stone', slug: 'stone' },
  { name: 'Ton', slug: 'ton' },
  { name: 'Cielo', slug: 'cielo' },
  { name: 'Rede', slug: 'rede' },
  { name: 'Getnet', slug: 'getnet' },
  { name: 'PagBank', slug: 'pagbank' },
  { name: 'Mercado Pago', slug: 'mercado-pago' },
  { name: 'SumUp', slug: 'sumup' },
  { name: 'InfinitePay', slug: 'infinitepay' },
  { name: 'SafraPay', slug: 'safrapay' },
  { name: 'C6 Pay', slug: 'c6-pay' },
  { name: 'Sicredi', slug: 'sicredi' },
  { name: 'Azulzinha', slug: 'azulzinha' },
  { name: 'Bin', slug: 'bin' },
  { name: 'Bradesco', slug: 'bradesco' },
  { name: 'Clover', slug: 'clover' },
  { name: 'Outra', slug: 'outra' },
];

/**
 * Chute inicial de taxa e prazo, no lugar de deixar o salao com a tela em
 * branco. Sao os valores mais comuns do mercado; o wizard pergunta os dois
 * principais e estes cobrem o resto.
 */
export const DEFAULT_MACHINE_RULES = {
  PIX: { feePercent: 0.99, daysToReceive: 0 },
  DEBIT: { feePercent: 1.99, daysToReceive: 1 },
  CREDIT: { feePercent: 3.19, daysToReceive: 30 },
} as const;

// ---------------------------------------------------------------------------
// Cupons
// ---------------------------------------------------------------------------

export const COUPON_TYPES = ['PERCENT', 'AMOUNT'] as const;

export type CouponTypeValue = (typeof COUPON_TYPES)[number];

export const COUPON_TYPE_LABELS: Record<CouponTypeValue, string> = {
  PERCENT: 'Percentual',
  AMOUNT: 'Valor fixo',
};

// ---------------------------------------------------------------------------
// Cashback
// ---------------------------------------------------------------------------

export const CASHBACK_ENTRY_TYPES = ['EARN', 'REDEEM'] as const;

export type CashbackEntryTypeValue = (typeof CASHBACK_ENTRY_TYPES)[number];

export const CASHBACK_ENTRY_TYPE_LABELS: Record<CashbackEntryTypeValue, string> = {
  EARN: 'Credito',
  REDEEM: 'Uso',
};

/** Dias da semana como o JS os numera - domingo e 0. */
export const WEEKDAY_LABELS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab'];

/** Rotulo curto do mes - cabe embaixo da barra do grafico. */
export const MONTH_SHORT_LABELS = [
  'jan',
  'fev',
  'mar',
  'abr',
  'mai',
  'jun',
  'jul',
  'ago',
  'set',
  'out',
  'nov',
  'dez',
];

/** Nome do mes por extenso - para titulo, nao para eixo de grafico. */
export const MONTH_LABELS = [
  'janeiro',
  'fevereiro',
  'marco',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
];

export const FINANCE_ENTRY_TYPES = ['PAYABLE', 'RECEIVABLE'] as const;

export type FinanceEntryTypeValue = (typeof FINANCE_ENTRY_TYPES)[number];

export const FINANCE_ENTRY_TYPE_LABELS: Record<FinanceEntryTypeValue, string> = {
  PAYABLE: 'A pagar',
  RECEIVABLE: 'A receber',
};

export const FINANCE_ENTRY_STATUSES = ['OPEN', 'PAID', 'CANCELLED'] as const;

export type FinanceEntryStatusValue = (typeof FINANCE_ENTRY_STATUSES)[number];

/** O rotulo da baixa muda com o lado da conta: quem paga nao "recebe". */
export const FINANCE_ENTRY_STATUS_LABELS: Record<FinanceEntryStatusValue, string> = {
  OPEN: 'Em aberto',
  PAID: 'Baixado',
  CANCELLED: 'Cancelado',
};

export const FINANCE_RECURRENCES = [
  'NONE',
  'WEEKLY',
  'MONTHLY',
  'QUARTERLY',
  'SEMIANNUAL',
  'YEARLY',
] as const;

export type FinanceRecurrenceValue = (typeof FINANCE_RECURRENCES)[number];

export const FINANCE_RECURRENCE_LABELS: Record<FinanceRecurrenceValue, string> = {
  NONE: 'Avulsa (uma vez)',
  WEEKLY: 'Semanal',
  MONTHLY: 'Mensal',
  QUARTERLY: 'Trimestral',
  SEMIANNUAL: 'Semestral',
  YEARLY: 'Anual',
};

/** Passo em meses de cada recorrencia; 0 = a conta anda de semana em semana. */
export const FINANCE_RECURRENCE_MONTHS: Record<FinanceRecurrenceValue, number> = {
  NONE: 0,
  WEEKLY: 0,
  MONTHLY: 1,
  QUARTERLY: 3,
  SEMIANNUAL: 6,
  YEARLY: 12,
};

/** Ate onde os vencimentos das contas recorrentes sao gerados de uma vez. */
export const FINANCE_FORECAST_MONTHS = 12;

/**
 * Categorias que toda empresa nova recebe - as contas do dia a dia de um salao.
 * Sao dados iniciais, nao uma lista fechada: o tenant edita, desativa e cria as
 * suas por cima.
 */
export const DEFAULT_FINANCE_CATEGORIES: readonly {
  name: string;
  type: FinanceEntryTypeValue;
}[] = [
  { name: 'Salario de Colaborador', type: 'PAYABLE' },
  { name: 'Comissao de Profissional', type: 'PAYABLE' },
  { name: 'Aluguel', type: 'PAYABLE' },
  { name: 'Condominio', type: 'PAYABLE' },
  { name: 'Luz', type: 'PAYABLE' },
  { name: 'Agua', type: 'PAYABLE' },
  { name: 'Internet', type: 'PAYABLE' },
  { name: 'Linha Telefonica', type: 'PAYABLE' },
  { name: 'Produtos e Insumos', type: 'PAYABLE' },
  { name: 'Equipamentos e Manutencao', type: 'PAYABLE' },
  { name: 'Marketing e Publicidade', type: 'PAYABLE' },
  { name: 'Impostos e Taxas', type: 'PAYABLE' },
  { name: 'Software e Sistemas', type: 'PAYABLE' },
  { name: 'Outras Despesas', type: 'PAYABLE' },
  { name: 'Servicos', type: 'RECEIVABLE' },
  { name: 'Venda de Produtos', type: 'RECEIVABLE' },
  { name: 'Pacotes e Assinaturas', type: 'RECEIVABLE' },
  { name: 'Outras Receitas', type: 'RECEIVABLE' },
];

export const SAAS_ADMIN_ROLE = 'SAAS_ADMIN';

export const COMPANY_USER_ROLES = ['ADMIN', 'MANAGER', 'MEMBER'] as const;

export const USER_ROLES = [...COMPANY_USER_ROLES, SAAS_ADMIN_ROLE] as const;

export type CompanyUserRole = (typeof COMPANY_USER_ROLES)[number];

export type UserRoleValue = (typeof USER_ROLES)[number];

export const USER_ROLE_LABELS: Record<UserRoleValue, string> = {
  ADMIN: 'Administrador',
  MANAGER: 'Gestor',
  MEMBER: 'Membro',
  SAAS_ADMIN: 'Administrador do SaaS',
};

export function isSaasAdmin(user: { role?: string | null } | null | undefined): boolean {
  return user?.role === SAAS_ADMIN_ROLE;
}

// ---------------------------------------------------------------------------
// Perfis, modulos e permissoes
//
// O papel (UserRole) continua sendo a hierarquia que os guards da API usam.
// Os perfis sao a camada configuravel: ficam nos grupos, e o usuario herda os
// perfis dos grupos a que pertence. A permissao efetiva de um usuario e a
// maior entre a do perfil-base do seu papel e a dos perfis dos seus grupos.
// ---------------------------------------------------------------------------

export const PROFILE_KEYS = ['COMMON', 'ATTENDANT', 'MANAGER', 'FULL_ADMIN', 'SUPPORT'] as const;

export type ProfileKeyValue = (typeof PROFILE_KEYS)[number];

export const PROFILE_LABELS: Record<ProfileKeyValue, string> = {
  COMMON: 'Usuario Comum',
  ATTENDANT: 'Atendente',
  MANAGER: 'Gestor',
  FULL_ADMIN: 'Full Admin',
  SUPPORT: 'Suporte',
};

export const PERMISSION_MODULES = [
  'users',
  'profiles',
  'settings',
  'customers',
  'services',
  'schedule',
  'appointments',
  'finance',
  'reports',
] as const;

export type PermissionModule = (typeof PERMISSION_MODULES)[number];

export const PERMISSION_MODULE_LABELS: Record<PermissionModule, string> = {
  users: 'Usuarios',
  profiles: 'Perfis de acesso',
  settings: 'Configuracoes',
  customers: 'Clientes',
  services: 'Servicos',
  schedule: 'Agenda',
  appointments: 'Atendimentos',
  finance: 'Financeiro',
  reports: 'Relatorios',
};

export const PERMISSION_LEVELS = ['NONE', 'READ', 'WRITE'] as const;

export type PermissionLevelValue = (typeof PERMISSION_LEVELS)[number];

export const PERMISSION_LEVEL_LABELS: Record<PermissionLevelValue, string> = {
  NONE: 'Sem acesso',
  READ: 'Consulta',
  WRITE: 'Escrita',
};

export const PAGINATION = {
  DEFAULT_PAGE: 1,
  DEFAULT_PER_PAGE: 20,
  MAX_PER_PAGE: 100,
} as const;

export const QUEUE_NAMES = {
  /**
   * Mensagem que chegou do cliente. Fica em fila propria porque quem a
   * processa e a API (o assistente precisa da agenda e do cadastro), e nao o
   * worker - o webhook so persiste e enfileira, e responde ao provedor na
   * hora.
   */
  INBOUND: 'inbound',
  EMAIL: 'email',
  REPORTS: 'reports',
  WEBHOOKS: 'webhooks',
  WHATSAPP: 'whatsapp',
} as const;

export type QueueName = (typeof QUEUE_NAMES)[keyof typeof QUEUE_NAMES];

export const JOB_NAMES = {
  WELCOME_EMAIL: 'welcome-email',
  PASSWORD_RESET_EMAIL: 'password-reset-email',
  PERIOD_REPORT: 'period-report',
  DISPATCH_WEBHOOK: 'dispatch-webhook',
  APPOINTMENT_MESSAGE: 'appointment-message',
  SEND_MESSAGE: 'send-message',
  CAMPAIGN_RUN: 'campaign-run',
  CAMPAIGN_SCHEDULED: 'campaign-scheduled',
  AUTOMATION_REMINDER: 'automation-reminder',
  AUTOMATION_CANCELED: 'automation-canceled',
  INBOUND_MESSAGE: 'inbound-message',
  UNREAD_CHECK: 'unread-check',
  TEMPLATE_APPROVAL_SYNC: 'template-approval-sync',
  PHONE_LINE_BILLING: 'phone-line-billing',
} as const;

export type JobName = (typeof JOB_NAMES)[keyof typeof JOB_NAMES];

/** Politica padrao de retentativa aplicada as filas BullMQ. */
export const DEFAULT_JOB_OPTIONS = {
  attempts: 3,
  backoff: { type: 'exponential', delay: 2000 },
  removeOnComplete: { age: 3600, count: 500 },
  removeOnFail: { age: 86400 },
} as const;

export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  ACCEPTED: 202,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  UNPROCESSABLE_ENTITY: 422,
  TOO_MANY_REQUESTS: 429,
  INTERNAL_SERVER_ERROR: 500,
} as const;

// ---------------------------------------------------------------------------
// Mensagens (WhatsApp)
// ---------------------------------------------------------------------------

export const MESSAGE_EVENTS = ['APPOINTMENT_CREATED', 'CAMPAIGN'] as const;

export type MessageEventValue = (typeof MESSAGE_EVENTS)[number];

export const MESSAGE_EVENT_LABELS: Record<MessageEventValue, string> = {
  APPOINTMENT_CREATED: 'Agendamento confirmado',
  CAMPAIGN: 'Campanha',
};

/**
 * Em que pe esta o template na Meta. Campanha e contato frio: fora das 24h
 * seguintes a mensagem do cliente, so sai o que ela aprovou - por isso a tela
 * da campanha acompanha isto, e nao so o texto.
 */
export const TEMPLATE_APPROVAL_KEYS = ['DRAFT', 'PENDING', 'APPROVED', 'REJECTED'] as const;

export type TemplateApprovalKey = (typeof TEMPLATE_APPROVAL_KEYS)[number];

export const TEMPLATE_APPROVAL_LABELS: Record<TemplateApprovalKey, string> = {
  DRAFT: 'Nao enviado',
  PENDING: 'Em analise',
  APPROVED: 'Aprovado',
  REJECTED: 'Reprovado',
};

/** O que cada situacao significa para o disparo, em uma linha. */
export const TEMPLATE_APPROVAL_HINTS: Record<TemplateApprovalKey, string> = {
  DRAFT: 'Ainda nao foi mandado para a Meta analisar.',
  PENDING: 'Na fila da Meta. Costuma responder em minutos, as vezes horas.',
  APPROVED: 'Pode disparar para qualquer cliente, a qualquer hora.',
  REJECTED: 'A Meta recusou. Corrija o texto e mande de novo.',
};

/** So aprovado alcanca quem nao escreveu nas ultimas 24h. */
export function templateAlcancaTodos(approval: TemplateApprovalKey | null | undefined): boolean {
  return approval === 'APPROVED';
}

/**
 * O que da para escrever no texto. A lista e fechada de proposito: o salao
 * escolhe entre estas, e nada mais chega ao cliente por engano.
 */
export const MESSAGE_VARIABLES = [
  { key: 'cliente', label: 'Nome do cliente', sample: 'Maria Silva' },
  { key: 'data', label: 'Data do atendimento', sample: '07/08/2026' },
  { key: 'hora', label: 'Hora do atendimento', sample: '14:30' },
  { key: 'profissional', label: 'Profissional', sample: 'Ana' },
  { key: 'servicos', label: 'Servicos', sample: 'Corte + escova' },
  { key: 'unidade', label: 'Unidade', sample: 'Matriz Pinheiros' },
  { key: 'endereco', label: 'Endereco da unidade', sample: 'Rua dos Pinheiros, 100' },
  { key: 'total', label: 'Total pago', sample: 'R$ 120,00' },
  {
    key: 'link_reagendamento',
    label: 'Link para reagendar',
    sample: 'https://…/landing-page/salao',
  },
  { key: 'cupom', label: 'Codigo do cupom', sample: 'VOLTA10' },
  { key: 'cupom_valor', label: 'Desconto do cupom', sample: '10%' },
  { key: 'cupom_validade', label: 'Validade do cupom', sample: '30/09/2026' },
  { key: 'link_agendamento', label: 'Link para agendar', sample: 'https://…/landing-page/salao' },
  { key: 'dias_sem_retorno', label: 'Dias sem retorno', sample: '75' },
] as const;

export type MessageVariableKey = (typeof MESSAGE_VARIABLES)[number]['key'];

/**
 * Troca as marcacoes pelo valor. Marcacao desconhecida sai como texto vazio -
 * mandar "{{promocao}}" para o cliente e pior do que a frase sem ela.
 */
export function renderMessage(body: string, values: Partial<Record<string, string>>): string {
  return body.replace(/\{\{\s*([a-z_]+)\s*\}\}/gi, (_, key: string) => values[key] ?? '');
}

/** As marcacoes usadas no texto, na ordem em que aparecem e sem repetir. */
export function messageVariablesOf(body: string): string[] {
  const found = [...body.matchAll(/\{\{\s*([a-z_]+)\s*\}\}/gi)].map((match) => match[1] as string);

  return [...new Set(found)];
}

/**
 * Estado do remetente de WhatsApp, como o provedor informa. Nao e derivado de
 * "tem numero salvo": um numero cadastrado pode estar esperando verificacao e
 * nao enviar nada, e dizer "Conectado" ali seria mentira na cara do usuario.
 */
export const WHATSAPP_SENDER_STATUS = {
  ONLINE: { label: 'Conectado', tone: 'ok' },
  'ONLINE:UPDATING': { label: 'Conectado, atualizando', tone: 'ok' },
  CREATING: { label: 'Preparando', tone: 'wait' },
  DRAFT: { label: 'Preparando', tone: 'wait' },
  STUBBED: { label: 'Preparando', tone: 'wait' },
  PENDING_VERIFICATION: { label: 'Aguardando verificacao', tone: 'wait' },
  VERIFYING: { label: 'Verificando o numero', tone: 'wait' },
  TWILIO_REVIEW: { label: 'Em analise', tone: 'wait' },
  OFFLINE: { label: 'Fora do ar', tone: 'bad' },
} as const;

export type WhatsappSenderStatus = keyof typeof WHATSAPP_SENDER_STATUS;

/** O que mostrar para um estado que ainda nao conhecemos. */
export function whatsappStatusLabel(status: string | null): { label: string; tone: string } {
  if (!status) return { label: 'Nao conectado', tone: 'none' };

  return WHATSAPP_SENDER_STATUS[status as WhatsappSenderStatus] ?? { label: status, tone: 'wait' };
}

/** So quem esta assim de fato entrega mensagem. */
export function whatsappCanSend(status: string | null): boolean {
  return status === 'ONLINE' || status === 'ONLINE:UPDATING';
}

/**
 * O que cada estado de mensagem quer dizer para quem esta no balcao. `tone`
 * escolhe a cor do risquinho ao lado do balao.
 */
export const MESSAGE_STATUS_LABELS = {
  QUEUED: { label: 'Enviando', tone: 'wait' },
  SENT: { label: 'Enviada', tone: 'wait' },
  DELIVERED: { label: 'Entregue', tone: 'ok' },
  READ: { label: 'Lida', tone: 'read' },
  FAILED: { label: 'Falhou', tone: 'error' },
} as const;

export type MessageStatusKey = keyof typeof MESSAGE_STATUS_LABELS;

export const MESSAGE_STATUS_KEYS = Object.keys(MESSAGE_STATUS_LABELS) as unknown as [
  MessageStatusKey,
  ...MessageStatusKey[],
];

/**
 * As colunas do funil, na ordem em que o contato caminha. A ordem manda no
 * quadro, no grafico e na regra que impede o lead de voltar.
 */
export const LEAD_STAGES = [
  {
    key: 'MESSAGE_SENT',
    label: 'Mensagem automatica disparada',
    hint: 'Recebeu campanha ou aviso do sistema',
  },
  { key: 'LEAD', label: 'Lead', hint: 'Escreveu e ainda nao foi atendido' },
  { key: 'AUTO_CONTACT', label: 'Contato automatico', hint: 'O assistente atendeu' },
  { key: 'HUMAN_CONTACT', label: 'Contato humano', hint: 'Uma pessoa do salao respondeu' },
  { key: 'SCHEDULED', label: 'Agendou', hint: 'Virou horario na agenda' },
] as const;

export type LeadStageKey = (typeof LEAD_STAGES)[number]['key'];

export const LEAD_STAGE_KEYS = LEAD_STAGES.map((stage) => stage.key) as unknown as [
  LeadStageKey,
  ...LeadStageKey[],
];

/**
 * As colunas do quadro. Nao e a mesma lista das etapas: quem atende e quem o
 * assistente atende moram na mesma coluna, e o filtro separa os dois quando o
 * salao quiser olhar so um lado.
 */
export const FUNNEL_COLUMNS = [
  {
    key: 'MESSAGE_SENT',
    label: 'Mensagem automatica disparada',
    hint: 'Recebeu campanha ou aviso do sistema',
    stages: ['MESSAGE_SENT'],
  },
  { key: 'LEAD', label: 'Lead', hint: 'Escreveu e ainda nao foi atendido', stages: ['LEAD'] },
  {
    key: 'CONTACT',
    label: 'Contato',
    hint: 'Ja foi atendido, pelo assistente ou por uma pessoa',
    stages: ['AUTO_CONTACT', 'HUMAN_CONTACT'],
  },
  { key: 'SCHEDULED', label: 'Agendou', hint: 'Virou horario na agenda', stages: ['SCHEDULED'] },
] as const satisfies readonly {
  key: string;
  label: string;
  hint: string;
  stages: readonly LeadStageKey[];
}[];

export type FunnelColumnKey = (typeof FUNNEL_COLUMNS)[number]['key'];

/** Recortes da coluna de contato. */
export const CONTACT_FILTERS = [
  { key: 'ALL', label: 'Todos' },
  { key: 'AUTO_CONTACT', label: 'Automatico' },
  { key: 'HUMAN_CONTACT', label: 'Humano' },
] as const;

export type ContactFilterKey = (typeof CONTACT_FILTERS)[number]['key'];

export function leadStageLabel(stage: string): string {
  return LEAD_STAGES.find((item) => item.key === stage)?.label ?? stage;
}

/** Posicao da etapa no funil. Quanto maior, mais adiante. */
export function leadStageRank(stage: LeadStageKey): number {
  return LEAD_STAGES.findIndex((item) => item.key === stage);
}

/**
 * O WhatsApp so aceita texto livre nas 24h seguintes a ultima mensagem do
 * cliente. Fora disso e preciso um modelo aprovado pela Meta.
 */
export const WHATSAPP_FREEFORM_WINDOW_MS = 24 * 60 * 60 * 1000;

/**
 * Formato internacional aceito pelo provedor. Quem escreve com `+` esta
 * dizendo o pais; numero solto assume o Brasil.
 */
export function normalizeWhatsappPhone(phone: string): string | null {
  const digits = phone.replace(/\D/g, '');
  if (digits.length < 10) return null;
  if (phone.trim().startsWith('+')) return `+${digits}`;

  return `+${digits.startsWith('55') ? digits : `55${digits}`}`;
}

/**
 * O caminho ate o salao estar pronto para operar, na ordem em que faz
 * sentido: servico antes de profissional, foto depois do servico cadastrado.
 * A instrucao cabe em duas linhas de proposito - guia que precisa ser lido
 * nao e guia.
 */
export const ONBOARDING_STEPS = [
  {
    key: 'UNITS',
    title: 'Completar Dados do Salão',
    hint: 'Endereço que o cliente vê na página pública.',
    href: '/unidades',
  },
  {
    key: 'CATEGORIES',
    title: 'Cadastrar categorias',
    hint: 'Como os servicos se organizam. O assistente ja traz uma lista pronta.',
    href: '/categorias',
  },
  {
    key: 'SERVICES',
    title: 'Cadastrar servicos',
    hint: 'Com duracao e preco. Eles aparecem no agendamento, no assistente e na pagina do salao.',
    href: '/servicos',
  },
  {
    key: 'PROFESSIONALS',
    title: 'Cadastrar profissionais',
    hint: 'Quem atende no salao. Cada um entra com o proprio acesso ao sistema.',
    href: '/usuarios',
  },
  {
    key: 'SCHEDULES',
    title: 'Configurar horarios',
    hint: 'Os dias e horarios de cada profissional. Sem isso a agenda nao oferece horario nenhum.',
    href: '/usuarios',
  },
  {
    key: 'LANDING',
    title: 'Publicar a pagina do salao',
    hint: 'O endereco que voce manda para o cliente marcar sozinho.',
    href: '/landing-page',
  },
  {
    key: 'WHATSAPP',
    title: 'Conectar o WhatsApp',
    hint: 'E por ele que saem confirmacoes, lembretes e campanhas.',
    href: '/whatsapp',
  },
  {
    key: 'CAMPAIGNS',
    title: 'Criar uma campanha',
    hint: 'A primeira mensagem para a base de clientes - o que traz gente de volta.',
    href: '/marketing/campanhas',
  },
] as const;

export type OnboardingStepKey = (typeof ONBOARDING_STEPS)[number]['key'];

export const ONBOARDING_STEP_KEYS = ONBOARDING_STEPS.map((item) => item.key) as unknown as [
  OnboardingStepKey,
  ...OnboardingStepKey[],
];

export const NOTIFICATION_TYPES = [
  { key: 'MESSAGE_UNREAD', label: 'Mensagem sem resposta' },
  { key: 'HUMAN_HANDOFF', label: 'Cliente pediu atendente' },
  { key: 'CAMPAIGN_FINISHED', label: 'Campanha concluida' },
  { key: 'APPOINTMENT_CANCELED', label: 'Horario cancelado' },
] as const;

export type NotificationTypeKey = (typeof NOTIFICATION_TYPES)[number]['key'];

export const NOTIFICATION_TYPE_KEYS = NOTIFICATION_TYPES.map((item) => item.key) as unknown as [
  NotificationTypeKey,
  ...NotificationTypeKey[],
];

/** Mensagem sem resposta por este tempo vira pendencia na tela. */
export const UNREAD_NOTICE_DELAY_MS = 30_000;

/**
 * Os avisos presos a um horario marcado.
 *
 * Recuperacao e aniversario sairam daqui: viraram objetivos de campanha, e
 * manter os dois lados ligados mandava a mesma mensagem duas vezes para o
 * mesmo cliente no mesmo dia. Estes tres ficam porque nao sao campanha -
 * nascem de um agendamento, e nao de um publico.
 */
export const AUTOMATIONS = [
  {
    key: 'APPOINTMENT_REMINDER',
    label: 'Lembrete de horario',
    hint: 'Lembra o cliente antes da hora',
    body: 'Oi {{cliente}}! Passando para lembrar do seu horario de {{servicos}} em {{data}} as {{hora}}, na {{unidade}}.',
  },
  {
    key: 'APPOINTMENT_CONFIRMED',
    label: 'Confirmacao de agendamento',
    hint: 'Sai assim que o horario e marcado',
    body: 'Oi {{cliente}}, seu horario de {{servicos}} esta marcado para {{data}} as {{hora}}, na {{unidade}}.',
  },
  {
    key: 'APPOINTMENT_CANCELED',
    label: 'Aviso de cancelamento',
    hint: 'Avisa quando o horario e desmarcado',
    body: 'Oi {{cliente}}, seu horario de {{data}} as {{hora}} foi cancelado. Se quiser remarcar, e so chamar aqui.',
  },
] as const;

export type AutomationTypeKey = (typeof AUTOMATIONS)[number]['key'];

export const AUTOMATION_TYPE_KEYS = AUTOMATIONS.map((item) => item.key) as unknown as [
  AutomationTypeKey,
  ...AutomationTypeKey[],
];

export function automationSuggestion(type: AutomationTypeKey): string {
  return AUTOMATIONS.find((item) => item.key === type)?.body ?? '';
}

export function automationLabel(type: string): string {
  return AUTOMATIONS.find((item) => item.key === type)?.label ?? type;
}

/** Quantos dias separam uma data da seguinte, na ordem em que vieram. */
export function intervalosEntre(datas: Date[]): number[] {
  const ordenadas = [...datas].sort((a, b) => a.getTime() - b.getTime());
  const dia = 24 * 60 * 60 * 1000;

  return ordenadas
    .slice(1)
    .map((data, indice) =>
      Math.round((data.getTime() - (ordenadas[indice] as Date).getTime()) / dia),
    );
}

/** Dias sem retorno usados quando ninguem escolheu um numero. */
export const DEFAULT_DAYS_WITHOUT_RETURN = 60;

/** Limites de quem sumiu: menos que isso e cedo demais, mais que isso e tarde. */
export const WINBACK_MIN_DAYS = 30;
export const WINBACK_MAX_DAYS = 180;

/**
 * De quantos em quantos dias este cliente costuma voltar, vezes 1.5 - e o
 * ponto em que ele ja passou da hora. Nao e modelo de IA: e a frequencia do
 * proprio cliente, que e o que a tela promete.
 *
 * Com menos de dois atendimentos nao ha frequencia para ler, e vale o numero
 * fixo que a empresa escolheu.
 */
export function winbackThresholdDays(
  intervalosEmDias: number[],
  fallbackDays: number = DEFAULT_DAYS_WITHOUT_RETURN,
): number {
  if (intervalosEmDias.length === 0) return fallbackDays;

  const media = intervalosEmDias.reduce((soma, dias) => soma + dias, 0) / intervalosEmDias.length;

  return Math.min(WINBACK_MAX_DAYS, Math.max(WINBACK_MIN_DAYS, Math.round(media * 1.5)));
}

/**
 * A hora em que a mensagem pode sair. Fora da janela ela espera a abertura do
 * dia seguinte - ninguem quer promocao as tres da manha.
 */
export function withinSendWindow(
  agora: Date,
  inicio: string | null | undefined,
  fim: string | null | undefined,
): boolean {
  if (!inicio || !fim) return true;

  const minutos = agora.getHours() * 60 + agora.getMinutes();
  const [horaInicio = 0, minutoInicio = 0] = inicio.split(':').map(Number);
  const [horaFim = 23, minutoFim = 59] = fim.split(':').map(Number);

  return minutos >= horaInicio * 60 + minutoInicio && minutos <= horaFim * 60 + minutoFim;
}

export const CAMPAIGN_STATUSES = [
  { key: 'DRAFT', label: 'Rascunho', hint: 'Ainda nao dispara' },
  { key: 'ACTIVE', label: 'Ativa', hint: 'Pode disparar' },
  { key: 'PAUSED', label: 'Pausada', hint: 'Guardada, sem disparar' },
  { key: 'ARCHIVED', label: 'Arquivada', hint: 'Fora de uso' },
] as const;

export type CampaignStatusKey = (typeof CAMPAIGN_STATUSES)[number]['key'];

export const CAMPAIGN_STATUS_KEYS = CAMPAIGN_STATUSES.map((item) => item.key) as unknown as [
  CampaignStatusKey,
  ...CampaignStatusKey[],
];

/**
 * Por que a campanha existe. E a primeira escolha, e dela sai o resto: o
 * nome, o publico e quando dispara.
 *
 * Antes cada campanha era uma folha em branco - nome livre, filtros a montar
 * e uma frequencia a escolher -, e o assinante tinha de saber de antemao que
 * publico queria. Partir do objetivo inverte isso: quem sabe dizer "quero meus
 * clientes antigos de volta" nao precisa saber traduzir aquilo em filtro.
 */
export const CAMPAIGN_OBJECTIVES = [
  {
    key: 'WINBACK',
    label: 'Recuperar clientes antigos',
    hint: 'Retorno de clientes que pararam de vir',
    audience: 'Clientes que ja vieram e nao voltam ha um tempo.',
  },
  {
    key: 'RECURRENCE',
    label: 'Recorrencia de novos clientes',
    hint: 'Incentivar segundo atendimento',
    audience: 'Quem foi atendido poucas vezes e nao voltou desde entao.',
  },
  {
    key: 'SPECIAL_DATE',
    label: 'Promocao de data especial',
    hint: 'Natal, ano novo, ferias',
    audience: 'Todos os clientes que aceitam WhatsApp.',
  },
  {
    key: 'BIRTHDAY',
    label: 'Aniversariantes',
    hint: 'Uma mensagem no dia do aniversario',
    audience: 'Quem faz aniversario no dia do disparo.',
  },
  {
    key: 'COUPON',
    // Sem recorte de publico nem gancho de servico ou data: o desconto vale
    // para a base inteira, no momento que o salao escolher.
    label: 'Promocao com cupom de desconto geral',
    hint: 'Vale para qualquer servico e qualquer profissional',
    audience: 'Todos os clientes que aceitam WhatsApp.',
  },
  {
    key: 'SERVICE',
    label: 'Promover um servico',
    hint: 'Cupom de desconto para um servico',
    audience: 'Clientes que ainda nao fizeram este servico.',
  },
] as const;

export type CampaignObjectiveKey = (typeof CAMPAIGN_OBJECTIVES)[number]['key'];

export const CAMPAIGN_OBJECTIVE_KEYS = CAMPAIGN_OBJECTIVES.map((item) => item.key) as unknown as [
  CampaignObjectiveKey,
  ...CampaignObjectiveKey[],
];

/**
 * As datas que o salao ja sabe que vai querer aproveitar, com o dia em que a
 * mensagem costuma valer - o disparo vem antes da data, porque promocao de
 * Natal entregue no dia 25 chega tarde para agendar.
 *
 * "Outra" existe porque nenhuma lista da conta do calendario de todo mundo:
 * dia das maes de um salao, aniversario da loja de outro.
 */
export const CAMPAIGN_SPECIAL_DATES = [
  { key: 'CHRISTMAS', label: 'Natal', day: 25, month: 12, nthSunday: null, daysBefore: 15 },
  { key: 'NEW_YEAR', label: 'Ano novo', day: 31, month: 12, nthSunday: null, daysBefore: 11 },
  { key: 'MOTHERS_DAY', label: 'Dia das maes', day: null, month: 5, nthSunday: 2, daysBefore: 10 },
  { key: 'FATHERS_DAY', label: 'Dia dos pais', day: null, month: 8, nthSunday: 2, daysBefore: 10 },
  { key: 'INDEPENDENCE', label: 'Independencia', day: 7, month: 9, nthSunday: null, daysBefore: 7 },
  {
    key: 'REPUBLIC',
    label: 'Proclamacao da Republica',
    day: 15,
    month: 11,
    nthSunday: null,
    daysBefore: 7,
  },
  {
    key: 'WINTER_BREAK',
    label: 'Ferias de meio de ano',
    day: 1,
    month: 7,
    nthSunday: null,
    daysBefore: 6,
  },
  {
    key: 'SUMMER_BREAK',
    label: 'Ferias de fim de ano',
    day: 15,
    month: 12,
    nthSunday: null,
    daysBefore: 14,
  },
  {
    key: 'OTHER',
    label: 'Outra data especial',
    day: null,
    month: null,
    nthSunday: null,
    daysBefore: 7,
  },
] as const;

export type CampaignSpecialDateKey = (typeof CAMPAIGN_SPECIAL_DATES)[number]['key'];

export const CAMPAIGN_SPECIAL_DATE_KEYS = CAMPAIGN_SPECIAL_DATES.map(
  (item) => item.key,
) as unknown as [CampaignSpecialDateKey, ...CampaignSpecialDateKey[]];

export const CUSTOMER_ORIGINS = [
  { key: 'COUNTER', label: 'Balcao' },
  { key: 'IMPORTED', label: 'Lista importada' },
  { key: 'SELF_SERVICE', label: 'Cadastro do proprio cliente' },
  { key: 'CAMPAIGN', label: 'Campanha' },
] as const;

export type CustomerOriginKey = (typeof CUSTOMER_ORIGINS)[number]['key'];

export const CUSTOMER_ORIGIN_KEYS = CUSTOMER_ORIGINS.map((item) => item.key) as unknown as [
  CustomerOriginKey,
  ...CustomerOriginKey[],
];

/**
 * O publico da campanha. "Nao informado" e uma escolha de verdade: em lista
 * importada a maior parte das pessoas nao tem sexo nem idade, e deixar essa
 * gente de fora por padrao esvaziaria o disparo sem avisar.
 */
export const CAMPAIGN_GENDERS = [
  { key: 'FEMALE', label: 'Feminino' },
  { key: 'MALE', label: 'Masculino' },
  { key: 'UNDISCLOSED', label: 'Nao informado' },
] as const;

export type CampaignGenderKey = (typeof CAMPAIGN_GENDERS)[number]['key'];

export const CAMPAIGN_GENDER_KEYS = CAMPAIGN_GENDERS.map((item) => item.key) as unknown as [
  CampaignGenderKey,
  ...CampaignGenderKey[],
];

export type CampaignFilters = {
  gender: CampaignGenderKey[];
  ageMin: number | null;
  ageMax: number | null;
  includeUnknownAge: boolean;
  origins: CustomerOriginKey[];
  unitIds: string[];
  /**
   * So quem nao volta ha tantos dias. Nulo desliga o recorte.
   *
   * "Nao voltar" pressupoe ter vindo: quem nunca marcou nada fica de fora,
   * porque para esse a conversa e outra - convite, nao reativacao.
   */
  inactiveDays: number | null;
  /** Recorrencia: dias desde o unico (ou ultimo) atendimento do novato. */
  sinceLastVisitDays: number | null;
  /**
   * Quantos dias antes da data a mensagem sai - do feriado, na data especial;
   * do aniversario, nos aniversariantes.
   *
   * Uma promocao de Natal entregue no dia 25 chega tarde para agendar, e o
   * quanto antes avisar depende do salao: quem trabalha com hora marcada
   * precisa de mais folga que quem atende por ordem de chegada.
   */
  daysBefore: number | null;
  /**
   * Deixa o sistema medir a janela cliente a cliente, em vez de um numero
   * igual para todo mundo.
   *
   * Um numero fixo trata quem corta o cabelo toda semana como quem vem duas
   * vezes por ano: com 45 dias, o primeiro so e chamado tarde demais e o
   * segundo e chamado antes da hora. Ligado, cada um e cobrado pelo proprio
   * ritmo.
   */
  autoDays: boolean;
  /** Servico que a campanha divulga - so no objetivo de servico. */
  serviceId: string | null;
  /** Profissional que a campanha divulga - so no objetivo de profissional. */
};

/** Dias sem retorno que a tela sugere quando o recorte e ligado. */
export const INACTIVE_DAYS_DEFAULT = 45;

/** Dias desde o atendimento que a recorrencia sugere. */
export const SINCE_LAST_VISIT_DEFAULT = 30;

/**
 * Quantos atendimentos ainda fazem de alguem um cliente novo. Acima disso a
 * pessoa ja tem habito, e a conversa deixa de ser "volte mais uma vez" para
 * virar reativacao.
 */
export const RECURRENCE_MAX_VISITS = 2;

/**
 * Quanto o cliente pode atrasar sobre o proprio ritmo antes de entrar na
 * lista. Um pouco de folga evita chamar quem so se atrasou uma semana.
 */
export const AUTO_DAYS_TOLERANCE = 1.5;

export const EMPTY_CAMPAIGN_FILTERS: CampaignFilters = {
  gender: [],
  ageMin: null,
  ageMax: null,
  includeUnknownAge: true,
  origins: [],
  unitIds: [],
  inactiveDays: null,
  sinceLastVisitDays: null,
  daysBefore: null,
  autoDays: true,
  serviceId: null,
};

/**
 * Os filtros voltam do banco como Json. Ler campo a campo e o que garante que
 * uma campanha gravada antes de um filtro novo continue valendo.
 */
export function toCampaignFilters(value: unknown): CampaignFilters {
  const raw = (value ?? {}) as Partial<CampaignFilters>;

  return {
    gender: raw.gender ?? EMPTY_CAMPAIGN_FILTERS.gender,
    ageMin: raw.ageMin ?? null,
    ageMax: raw.ageMax ?? null,
    includeUnknownAge: raw.includeUnknownAge ?? EMPTY_CAMPAIGN_FILTERS.includeUnknownAge,
    origins: raw.origins ?? EMPTY_CAMPAIGN_FILTERS.origins,
    unitIds: raw.unitIds ?? EMPTY_CAMPAIGN_FILTERS.unitIds,
    inactiveDays: raw.inactiveDays ?? null,
    sinceLastVisitDays: raw.sinceLastVisitDays ?? null,
    daysBefore: raw.daysBefore ?? null,
    autoDays: raw.autoDays ?? EMPTY_CAMPAIGN_FILTERS.autoDays,
    serviceId: raw.serviceId ?? null,
  };
}

/** O que a campanha precisa saber de si para se nomear e se anunciar. */
export type CampaignNaming = {
  objective: CampaignObjectiveKey;
  inactiveDays?: number | null;
  sinceLastVisitDays?: number | null;
  autoDays?: boolean;
  /** Nome do servico - vem do banco, nao daqui. */
  serviceName?: string | null;
  specialDate?: CampaignSpecialDateKey | null;
  scheduledAt?: Date | string | null;
};

/** A data como `dd/mm`, para caber no nome sem virar frase. */
function diaEMes(value: Date | string): string {
  const date = typeof value === 'string' ? new Date(value) : value;

  return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * O nome da campanha, escrito pelo objetivo.
 *
 * Ninguem batiza mais campanha na mao. Nome livre produzia lista de "teste",
 * "teste 2" e "promo nova" - e, pior, dizia menos do que o proprio objetivo ja
 * diz. Como o nome sai dos mesmos campos que definem o publico, ele muda junto
 * quando o publico muda, e duas campanhas diferentes nunca aparecem iguais.
 */
export function campaignAutoName(naming: CampaignNaming, hoje = new Date()): string {
  const {
    objective,
    inactiveDays,
    sinceLastVisitDays,
    autoDays,
    serviceName,
    specialDate,
    scheduledAt,
  } = naming;

  if (objective === 'WINBACK') {
    return autoDays
      ? 'Recuperar clientes - ritmo de cada um'
      : `Recuperar clientes - ${inactiveDays ?? INACTIVE_DAYS_DEFAULT}+ dias sem retorno`;
  }

  if (objective === 'RECURRENCE') {
    return autoDays
      ? 'Recorrencia de novos - ritmo de cada um'
      : `Recorrencia de novos - ${sinceLastVisitDays ?? SINCE_LAST_VISIT_DEFAULT} dias do atendimento`;
  }

  if (objective === 'SERVICE') {
    return serviceName ? `Promocao de ${serviceName}` : 'Promocao de servico';
  }

  if (objective === 'SPECIAL_DATE') {
    const data = CAMPAIGN_SPECIAL_DATES.find((item) => item.key === specialDate);

    if (data && data.key !== 'OTHER') return `Promocao de ${data.label}`;

    return scheduledAt ? `Data especial de ${diaEMes(scheduledAt)}` : 'Promocao de data especial';
  }

  if (objective === 'BIRTHDAY') return 'Aniversariantes';

  // O desconto geral nao tem recorte que o descreva: sobra o mes em que foi
  // criado, que ao menos separa o de agosto do de setembro na lista.
  return `Promocao de ${MONTH_LABELS[hoje.getMonth()]}`;
}

/** Quantos dias antes a mensagem sai, por objetivo. Nulo para quem nao espera. */
export function campaignDaysBefore(
  objective: CampaignObjectiveKey,
  escolhido: number | null | undefined,
): number | null {
  if (objective === 'BIRTHDAY') return escolhido ?? BIRTHDAY_DAYS_BEFORE_DEFAULT;
  if (objective === 'SPECIAL_DATE') return escolhido ?? 0;

  return null;
}

/**
 * Os filtros que o objetivo exige, e nenhum dos que ele nao usa.
 *
 * Duas coisas ao mesmo tempo: semear o padrao (quem escolhe "recuperar
 * clientes" ja quer os 45 dias no campo, nao um campo vazio com o nome
 * dizendo 45) e apagar o que sobrou do objetivo anterior - um servico
 * escolhido que continuasse gravado depois de virar campanha de aniversario
 * seguiria filtrando o publico sem aparecer em lugar nenhum.
 */
export function campaignFiltersFor(
  objective: CampaignObjectiveKey,
  filters: CampaignFilters,
): CampaignFilters {
  return {
    ...filters,
    inactiveDays: objective === 'WINBACK' ? (filters.inactiveDays ?? INACTIVE_DAYS_DEFAULT) : null,
    sinceLastVisitDays:
      objective === 'RECURRENCE' ? (filters.sinceLastVisitDays ?? SINCE_LAST_VISIT_DEFAULT) : null,
    autoDays: objective === 'WINBACK' || objective === 'RECURRENCE' ? filters.autoDays : false,
    daysBefore: campaignDaysBefore(objective, filters.daysBefore),
    serviceId: objective === 'SERVICE' ? filters.serviceId : null,
  };
}

/** So a data especial escolhe um dia - as manuais saem no clique. */
export function campaignUsesDate(objective: CampaignObjectiveKey): boolean {
  return objective === 'SPECIAL_DATE';
}

/** As que esperam alguem clicar em Disparar. */
export function campaignIsManual(objective: CampaignObjectiveKey): boolean {
  return objective === 'COUPON' || objective === 'SERVICE';
}

/**
 * Quando a campanha vai sair, em uma frase.
 *
 * A tela nao pergunta mais "manual ou mensal": o objetivo ja respondeu. O que
 * ela faz e contar o que ficou decidido, para ninguem salvar achando que
 * dispara sozinho quando nao dispara.
 */
export function campaignDispatchLabel(
  objective: CampaignObjectiveKey,
  scheduledAt?: Date | string | null,
): string {
  if (campaignIsManual(objective)) return 'Dispara quando voce clicar em Disparar';
  if (objective === 'BIRTHDAY') return 'Dispara automaticamente no aniversario de cada cliente';

  if (objective === 'WINBACK' || objective === 'RECURRENCE') {
    return 'Dispara automaticamente todo dia, para quem entrar na janela';
  }

  if (!scheduledAt) return 'Escolha a data do disparo';

  const data = typeof scheduledAt === 'string' ? new Date(scheduledAt) : scheduledAt;

  return `Dispara automaticamente em ${new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' }).format(data)}`;
}

/**
 * Quantos dias antes do aniversario a mensagem sai.
 *
 * Bem antes da data: quem faz aniversario ja tem o dia tomado, e o convite
 * precisa chegar a tempo de virar horario marcado na semana que antecede.
 */
export const BIRTHDAY_DAYS_BEFORE_DEFAULT = 20;

/** Quantos dias antes a data especial sugere mandar a mensagem. */
export function specialDateDaysBefore(key: CampaignSpecialDateKey): number {
  return CAMPAIGN_SPECIAL_DATES.find((item) => item.key === key)?.daysBefore ?? 0;
}

/** "Natal (25/12)" - a data cabe no proprio nome da opcao. */
export function specialDateOption(key: CampaignSpecialDateKey, year: number): string {
  const data = CAMPAIGN_SPECIAL_DATES.find((item) => item.key === key);
  if (!data) return key;

  const dia = specialDateOf(key, year);
  if (!dia) return data.label;

  return `${data.label} (${dia.getDate()} de ${MONTH_LABELS[dia.getMonth()]})`;
}

/** O dia do ano em que a data especial escolhida cai, no ano informado. */
export function specialDateOf(key: CampaignSpecialDateKey, year: number): Date | null {
  const data = CAMPAIGN_SPECIAL_DATES.find((item) => item.key === key);
  if (!data || data.month === null) return null;

  if (data.nthSunday !== null) {
    // O primeiro domingo do mes, mais uma semana por ordinal. `getDay()` do
    // dia 1 diz quantos dias faltam ate ele.
    const primeiro = new Date(year, data.month - 1, 1);
    const ateDomingo = (7 - primeiro.getDay()) % 7;

    return new Date(year, data.month - 1, 1 + ateDomingo + (data.nthSunday - 1) * 7);
  }

  if (data.day === null) return null;

  return new Date(year, data.month - 1, data.day);
}

export function campaignStatusLabel(status: string): string {
  return CAMPAIGN_STATUSES.find((item) => item.key === status)?.label ?? status;
}

/**
 * O cupom que cada objetivo pede, ja escrito.
 *
 * Campanha sem desconto e so um lembrete; com desconto, e um motivo para
 * voltar. Mas escolher codigo e percentual do zero, toda vez, e o tipo de
 * trabalho que faz a pessoa pular a etapa - e disparar sem oferta nenhuma.
 * Aqui vem uma sugestao pronta e coerente com o objetivo; o codigo continua
 * editavel, porque o nome do cupom e do salao, nao nosso.
 */
export const CAMPAIGN_COUPON_SUGGESTIONS: Record<
  CampaignObjectiveKey,
  { code: string; percent: number; description: string }
> = {
  COUPON: { code: 'DESCONTO', percent: 10, description: 'Desconto para toda a base' },
  WINBACK: { code: 'QUESAUDADE', percent: 20, description: 'Desconto de boas-vindas de volta' },
  RECURRENCE: { code: 'VOLTESEMPRE', percent: 15, description: 'Desconto do segundo atendimento' },
  SERVICE: {
    code: 'EXPERIMENTE',
    percent: 15,
    description: 'Desconto para experimentar o servico',
  },
  SPECIAL_DATE: { code: 'DATAESPECIAL', percent: 10, description: 'Desconto da data especial' },
  BIRTHDAY: { code: 'PARABENS', percent: 20, description: 'Presente de aniversario' },
};

/** Os codigos que as datas comemorativas pedem - mais reconheciveis que o generico. */
const CODIGO_DA_DATA: Record<CampaignSpecialDateKey, string> = {
  CHRISTMAS: 'NATAL',
  NEW_YEAR: 'ANONOVO',
  MOTHERS_DAY: 'MAES',
  FATHERS_DAY: 'PAIS',
  INDEPENDENCE: 'SETEDESETEMBRO',
  REPUBLIC: 'FERIADO',
  WINTER_BREAK: 'FERIAS',
  SUMMER_BREAK: 'FERIAS',
  OTHER: 'DATAESPECIAL',
};

export function campaignCouponSuggestion(
  objective: CampaignObjectiveKey,
  specialDate?: CampaignSpecialDateKey | null,
): { code: string; percent: number; description: string } {
  const base = CAMPAIGN_COUPON_SUGGESTIONS[objective];

  if (objective === 'SPECIAL_DATE' && specialDate) {
    return {
      ...base,
      code: CODIGO_DA_DATA[specialDate],
      description: `Desconto de ${campaignSpecialDateLabel(specialDate)}`,
    };
  }

  return base;
}

export function campaignObjectiveLabel(objective: string): string {
  return CAMPAIGN_OBJECTIVES.find((item) => item.key === objective)?.label ?? objective;
}

export function campaignSpecialDateLabel(key: string): string {
  return CAMPAIGN_SPECIAL_DATES.find((item) => item.key === key)?.label ?? key;
}

export function customerOriginLabel(origin: string): string {
  return CUSTOMER_ORIGINS.find((item) => item.key === origin)?.label ?? origin;
}

/**
 * Ninguem recebe duas campanhas no mesmo dia: uma lista que aparece varias
 * vezes na caixa de entrada vira bloqueio, e bloqueio derruba o numero.
 */
export const CAMPAIGN_COOLDOWN_MS = 24 * 60 * 60 * 1000;

/**
 * As marcacoes que a campanha entende. E um conjunto menor que o da
 * confirmacao de horario: no disparo nao existe atendimento marcado, entao
 * data, hora e profissional sairiam vazios.
 */
export const CAMPAIGN_VARIABLES = [
  { key: 'cliente', label: 'Primeiro nome', sample: 'Maria' },
  { key: 'unidade', label: 'Unidade', sample: 'Matriz Pinheiros' },
  { key: 'cupom', label: 'Codigo do cupom', sample: 'VOLTA10' },
  { key: 'cupom_valor', label: 'Desconto do cupom', sample: '10%' },
  { key: 'link_reagendamento', label: 'Link da pagina', sample: 'https://…/salao' },
] as const;

/**
 * O desconto em palavras: "10%" ou "R$ 20,00". Mora aqui porque a campanha e a
 * automacao escrevem a mesma frase para o mesmo cupom.
 */
export function couponValueLabel(
  coupon: { type: string; value: unknown } | null | undefined,
): string {
  if (!coupon) return '';

  const valor = Number(coupon.value);

  return coupon.type === 'PERCENT'
    ? `${valor.toLocaleString('pt-BR')}%`
    : valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

/**
 * A faixa de idade vira faixa de ano de nascimento - e o que o cadastro tem
 * para filtrar. A idade e a que a pessoa completa no ano, sem o dia: metade
 * das listas so traz o ano, e cobrar precisao de dia deixaria essa gente
 * fora do publico.
 */
export function birthYearRange(
  ageMin: number | null,
  ageMax: number | null,
  today: Date,
): { min: number | null; max: number | null } {
  const ano = today.getFullYear();

  return {
    min: ageMax === null ? null : ano - ageMax,
    max: ageMin === null ? null : ano - ageMin,
  };
}

/** Idade que a pessoa completa neste ano. Nulo quando o ano nao foi informado. */
export function ageFromBirthYear(birthYear: number | null | undefined, today: Date): number | null {
  return birthYear ? today.getFullYear() - birthYear : null;
}
